// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import {
  DepreciationScheduleReport,
  depreciationScheduleAssetSchema,
  depreciationSchedulePeriodSchema,
} from '@/report/api/types';
import { parseArrayInChunks } from '@/utilities/chunked-parse';
import { DateTime } from 'luxon';
import queryString from 'query-string';
import { z } from 'zod';

const dynamicDepreciationScheduleAssetSchema = z
  .any()
  .transform((asset): unknown => asset);

const depreciationScheduleResponseSchema = z
  .object({
    data: z.object({
      periods: z.array(depreciationSchedulePeriodSchema),
      assets: z.array(dynamicDepreciationScheduleAssetSchema),
    }),
    metadata: responseMetadataSchema,
  })
  .transform(({ data }) => data);

interface GetDepreciationScheduleReportParameters {
  startDate: DateTime;
  endDate: DateTime;
  itemKeys?: string[];
  locationKeys?: string[];
  currency?: IsoCurrencyCode;
}

export async function getDepreciationScheduleReport(
  parameters: GetDepreciationScheduleReportParameters
): Promise<DepreciationScheduleReport> {
  const startDate = parameters.startDate.toISODate();
  const endDate = parameters.endDate.toISODate();
  const path = '/v1/report/asset/depreciation-schedule';
  const response = await api.get(path, {
    paramsSerializer: (params) => queryString.stringify(params),
    params: {
      startDate,
      endDate,
      itemKeys:
        parameters.itemKeys && parameters.itemKeys.length > 0
          ? parameters.itemKeys
          : undefined,
      locationKeys:
        parameters.locationKeys && parameters.locationKeys.length > 0
          ? parameters.locationKeys
          : undefined,
      currency: parameters.currency,
    },
  });

  const data = await parseResponse(
    depreciationScheduleResponseSchema,
    response.data,
    {
      schemaName: 'DepreciationScheduleReport',
      endpoint: path,
    }
  );

  const assets = await parseArrayInChunks(
    data.assets,
    depreciationScheduleAssetSchema,
    {
      schemaValidation: {
        schemaName: 'DepreciationScheduleAsset',
        endpoint: path,
        extra: {
          startDate,
          endDate,
        },
      },
    }
  );

  return {
    periods: data.periods,
    assets,
  };
}
